import React from 'react';
import { motion } from 'framer-motion';
import { scrollToId } from '@/hooks/useLenis';

const blocks = [
    {
        n: '01',
        tag: 'Antes de FITUR',
        title: 'Preparación estratégica',
        summary: 'Sesiones de consultoría virtual con tu equipo para llegar a Madrid con todo listo.',
        items: [
            'Diagnóstico comercial y de internacionalización',
            'Definición de mercados y objetivos medibles',
            'Optimización del pitch y materiales comerciales',
            'Agenda B2B pre-armada con compradores europeos',
            'Onboarding a la Tribu y coordinación logística',
        ],
    },
    {
        n: '02',
        tag: 'Durante FITUR',
        title: 'Ejecución en Madrid',
        summary: 'Trabajo diario en terreno junto al equipo Incubatour durante toda la feria.',
        items: [
            'Acompañamiento presencial cada día en IFEMA',
            'Ejecución de reuniones B2B pre-agendadas',
            'Navegación estratégica de pabellones + LiveConnect',
            'Revisión diaria con la Tribu para ajustar',
            'Networking curado con compradores y la Tribu',
        ],
    },
    {
        n: '03',
        tag: 'Después de FITUR',
        title: 'Seguimiento y cierre',
        summary: 'Convertir conversaciones en negocios reales con acompañamiento sostenido.',
        items: [
            'Sistematización con IA de todos los contactos',
            'Roadmap comercial personalizado post-feria',
            'Sesiones de nurturing de conversaciones clave',
            'Recomendaciones concretas de escala',
            'Reporte final con oportunidades identificadas',
        ],
    },
];

const WhatIs = () => (
    <section id="que-es" className="relative py-14 md:py-18 border-t border-white/10 bg-cream overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="grid grid-cols-12 gap-5 md:gap-8 mb-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="col-span-12 md:col-span-7"
                >
                    <div className="eyebrow-brand mb-4">¿Qué es la Misión?</div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95]" style={{ color: '#1A1815' }}>
                        No es un viaje. Es <span className="italic-accent text-[color:var(--brand)] font-normal">consultoría</span><br />
                        de internacionalización.
                    </h2>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="col-span-12 md:col-span-5 md:pt-2"
                >
                    <p className="text-base md:text-lg leading-relaxed" style={{ color: '#4A4238' }}>
                        Un programa práctico en 3 fases con actividades concretas antes, durante y
                        después de FITUR. Estás contratando un método, no una entrada a la feria.
                    </p>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {blocks.map((b, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className="relative border border-white/12 bg-[color:var(--bg)] p-6 md:p-8 flex flex-col gap-4"
                    >
                        <div className="flex items-baseline justify-between">
                            <span className="text-[color:var(--brand)] num-display text-3xl md:text-4xl">
                                {b.n}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">
                                {b.tag}
                            </span>
                        </div>
                        <div>
                            <div className="font-display text-white text-2xl md:text-3xl mb-3 leading-tight">
                                {b.title}
                            </div>
                            <p className="text-white/70 text-sm leading-relaxed mb-5">
                                {b.summary}
                            </p>
                        </div>
                        <ul className="space-y-2.5 pt-4 border-t border-white/8">
                            {b.items.map((it, k) => (
                                <li key={k} className="flex items-start gap-2.5 text-sm text-white/85 leading-snug">
                                    <span className="mt-1.5 h-[1px] w-3 flex-shrink-0 bg-[color:var(--brand)]" />
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-l-2 border-[color:var(--brand)] p-5 md:p-6"
                style={{ background: 'rgba(26,24,21,0.05)' }}
            >
                <div className="text-sm md:text-base leading-relaxed max-w-[62ch]" style={{ color: '#3A3428' }}>
                    <strong style={{ color: '#1A1815' }}>Lo que compras</strong> es un método probado
                    guiado por expertos + IA aplicada + acompañamiento diario.
                    El pasaje aéreo lo pones tú; nosotros ponemos el retorno.
                </div>
                <button
                    onClick={() => scrollToId('fases')}
                    className="inline-flex items-center gap-2 rounded-full border-2 border-[#1A1815] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] hover:bg-[#1A1815] hover:text-white transition flex-shrink-0"
                    style={{ color: '#1A1815' }}
                >
                    Ver detalle de fases
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </motion.div>
        </div>
    </section>
);

export default WhatIs;
